import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { connect } from 'react-redux';
import styled from 'styled-components';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';

import firebase from '../../Firebase/Firebase';
import FirebaseImage from '../Media/FirebaseImage';
import { deleteUploadedFiles, uploadProjectMessageImages, validateImage } from '../../Utils/uploads';
import { useI18n } from '../../i18n/I18nProvider';

const toDate = (value) => value?.toDate?.() || (value ? new Date(value) : null);

const ProjectConversationPanel = ({ demand, firebaseState }) => {
  const { t, formatDate, pick } = useI18n();
  const [messages, setMessages] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [body, setBody] = useState('');
  const [files, setFiles] = useState([]);
  const [viewer, setViewer] = useState(null);
  const [galleryError, setGalleryError] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const listRef = useRef(null);
  const filesRef = useRef([]);
  const pointerStartRef = useRef(null);
  const uid = firebaseState?.auth?.uid;
  const profile = firebaseState?.profile || {};
  const role = profile?.user_type || '';
  const demandId = demand?.demand_id;
  const viewerOpen = Boolean(viewer);

  const allowed = useMemo(() => {
    const artisanId = demand?.contract?.artisan_id || demand?.estimation?.artisan_id || demand?.selected_artisan_id;
    return Boolean(uid && demandId && (demand?.user_id === uid || artisanId === uid));
  }, [demand, demandId, uid]);

  useEffect(() => {
    if (!allowed) return undefined;
    return firebase.firestore()
      .collection('demands').doc(demandId)
      .collection('messages')
      .orderBy('created_at', 'asc')
      .limit(150)
      .onSnapshot(
        (snapshot) => setMessages(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))),
        (nextError) => setError(nextError?.message || t('conversation.loadError')),
      );
  }, [allowed, demandId, t]);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => { filesRef.current = files; }, [files]);
  useEffect(() => () => filesRef.current.forEach((item) => URL.revokeObjectURL(item.preview)), []);

  useEffect(() => {
    if (!viewerOpen || typeof document === 'undefined') return undefined;
    const body = document.body;
    const root = document.documentElement;
    const scrollY = window.scrollY;
    const previousBody = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
      touchAction: body.style.touchAction,
    };
    const previousRoot = {
      overflow: root.style.overflow,
      overscrollBehavior: root.style.overscrollBehavior,
    };

    window.dispatchEvent(new CustomEvent('lpb:immersive-state', { detail: { open: true, source: 'gallery' } }));
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    body.style.touchAction = 'none';
    root.style.overflow = 'hidden';
    root.style.overscrollBehavior = 'none';

    const onKeyDown = (event) => {
      if (event.key === 'Escape') setViewer(null);
      if (event.key === 'ArrowLeft') setViewer((current) => current && ({ ...current, index: (current.index - 1 + current.attachments.length) % current.attachments.length }));
      if (event.key === 'ArrowRight') setViewer((current) => current && ({ ...current, index: (current.index + 1) % current.attachments.length }));
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      Object.assign(body.style, previousBody);
      Object.assign(root.style, previousRoot);
      window.dispatchEvent(new CustomEvent('lpb:immersive-state', { detail: { open: false, source: 'gallery' } }));
      window.requestAnimationFrame(() => window.scrollTo({ top: scrollY, left: 0, behavior: 'auto' }));
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [viewerOpen]);

  if (!allowed) return null;

  const chooseFiles = (event) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = '';
    setError('');
    try {
      const room = Math.max(0, 4 - files.length);
      const next = selected.slice(0, room).map((file) => {
        validateImage(file);
        return { file, preview: URL.createObjectURL(file) };
      });
      setFiles((current) => [...current, ...next]);
      if (selected.length > room) setError(t('conversation.maxPhotos'));
    } catch (nextError) {
      setError(nextError?.message || t('conversation.photoError'));
    }
  };

  const removeFile = (index) => {
    setFiles((current) => current.filter((item, itemIndex) => {
      if (itemIndex === index) URL.revokeObjectURL(item.preview);
      return itemIndex !== index;
    }));
  };

  const send = async () => {
    const text = body.trim();
    if ((!text && !files.length) || busy) return;
    setBusy(true);
    setProgress(0);
    setError('');
    let attachments = [];
    try {
      attachments = await uploadProjectMessageImages(files.map((item) => item.file), demandId, setProgress);
      await firebase.firestore()
        .collection('demands').doc(demandId)
        .collection('messages')
        .add({
          demand_id: demandId,
          sender_id: uid,
          sender_role: role,
          body: text.slice(0, 1500),
          attachments,
          created_at: firebase.firestore.FieldValue.serverTimestamp(),
        });
      files.forEach((item) => URL.revokeObjectURL(item.preview));
      setFiles([]);
      setBody('');
    } catch (nextError) {
      await deleteUploadedFiles(attachments.map((item) => item.storage_path));
      setError(nextError?.message || t('conversation.sendError'));
    } finally {
      setBusy(false);
      setProgress(0);
    }
  };

  const moveViewer = (direction) => {
    setViewer((current) => {
      if (!current?.attachments?.length) return current;
      const index = (current.index + direction + current.attachments.length) % current.attachments.length;
      return { ...current, index };
    });
  };

  const downloadCurrentImage = async () => {
    const attachment = viewer?.attachments?.[viewer.index];
    if (!attachment?.url) return;
    setGalleryError('');
    const filename = attachment.name || `photo-projet-${viewer.index + 1}.jpg`;

    try {
      const response = await fetch(attachment.url, { mode: 'cors', cache: 'no-store' });
      if (!response.ok) throw new Error('download-failed');
      const blob = await response.blob();
      const file = new File([blob], filename, { type: blob.type || attachment.content_type || 'image/jpeg' });

      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: filename });
        return;
      }

      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
    } catch (nextError) {
      if (nextError?.name === 'AbortError') return;
      if (navigator.share) {
        try {
          await navigator.share({ title: filename, url: attachment.url });
          return;
        } catch (shareError) {
          if (shareError?.name === 'AbortError') return;
        }
      }
      setGalleryError(t('conversation.directSaveError'));
    }
  };

  const openCurrentImage = () => {
    const attachment = viewer?.attachments?.[viewer.index];
    if (attachment?.url) window.open(attachment.url, '_blank', 'noopener,noreferrer');
  };

  const onGalleryPointerDown = (event) => {
    if (event.pointerType === 'mouse') return;
    pointerStartRef.current = { x: event.clientX, y: event.clientY };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const onGalleryPointerUp = (event) => {
    const start = pointerStartRef.current;
    pointerStartRef.current = null;
    if (!start) return;
    const deltaX = event.clientX - start.x;
    const deltaY = event.clientY - start.y;
    if (Math.abs(deltaY) > 85 && Math.abs(deltaY) > Math.abs(deltaX)) {
      setViewer(null);
      return;
    }
    if (viewer?.attachments?.length < 2 || Math.abs(deltaX) < 38 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
    moveViewer(deltaX < 0 ? 1 : -1);
  };

  const currentAttachment = viewer?.attachments?.[viewer.index];
  const lastMessage = messages[messages.length - 1] || null;
  const lastMessagePreview = lastMessage?.body?.trim()
    || (Array.isArray(lastMessage?.attachments) && lastMessage.attachments.length
      ? pick('Photo partagée', 'Shared photo')
      : '');

  return (
    <>
      <Panel $expanded={expanded}>
        <ConversationToggle
          type="button"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
        >
          <ChatBubbleOutlineOutlinedIcon />
          <ConversationHeading>
            <strong>{t('conversation.title')}</strong>
            <span>
              {messages.length > 0
                ? `${messages.length} ${pick(messages.length > 1 ? 'messages' : 'message', messages.length > 1 ? 'messages' : 'message')}${lastMessagePreview ? ` · ${lastMessagePreview}` : ''}`
                : t('conversation.subtitle')}
            </span>
          </ConversationHeading>
          <ToggleIcon $expanded={expanded}><ExpandMoreRoundedIcon /></ToggleIcon>
        </ConversationToggle>

        {expanded && (
          <ConversationBody>
            <MessageList ref={listRef}>
              {messages.length === 0 ? (
                <Empty>{t('conversation.empty')}</Empty>
              ) : messages.map((message) => {
                const own = message.sender_id === uid;
                const date = toDate(message.created_at);
                const attachments = Array.isArray(message.attachments) ? message.attachments : [];
                return (
                  <Message key={message.id} $own={own}>
                    <MessageAuthor>
                      {!own && <MiniAvatar src={message.verified_sender_profile_image} fallbackSrc="/images/user.svg" alt="" />}
                      <strong>{own ? t('conversation.you') : message.verified_sender_name || (message.sender_role === 'Artisan' ? t('common.professional') : t('common.client'))}</strong>
                    </MessageAuthor>
                    {message.body && <p className="project-message__body">{message.body}</p>}
                    {attachments.length > 0 && (
                      <AttachmentGrid $count={attachments.length}>
                        {attachments.map((attachment, index) => (
                          <AttachmentButton
                            key={`${attachment.url}-${index}`}
                            type="button"
                            onClick={() => { setGalleryError(''); setViewer({ attachments, index, sender: own ? t('conversation.you') : message.verified_sender_name || t('conversation.participant') }); }}
                            aria-label={t('conversation.showPhoto', { index: index + 1 })}
                          >
                            <FirebaseImage src={attachment.url} fallbackSrc="/images/project-placeholder.svg" alt={attachment.name || `${t('common.photo')} ${index + 1}`} />
                            <AttachmentBadge>{index + 1}/{attachments.length}</AttachmentBadge>
                          </AttachmentButton>
                        ))}
                      </AttachmentGrid>
                    )}
                    <small>{date ? formatDate(date, { dateStyle: 'short', timeStyle: 'short' }) : t('conversation.sentAt')}</small>
                  </Message>
                );
              })}
            </MessageList>

            {files.length > 0 && (
              <Previews>
                {files.map((item, index) => (
                  <Preview key={item.preview}>
                    <img src={item.preview} alt={t('conversation.previewAlt')} />
                    <button type="button" onClick={() => removeFile(index)} aria-label={t('conversation.removePhoto')}><CloseOutlinedIcon /></button>
                  </Preview>
                ))}
              </Previews>
            )}

            <Composer>
              <textarea
                value={body}
                onChange={(event) => setBody(event.target.value)}
                onKeyDown={(event) => {
                  if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                    event.preventDefault();
                    send();
                  }
                }}
                placeholder={t('conversation.placeholder')}
                maxLength={1500}
                rows={2}
              />
              <Actions>
                <PhotoButton aria-label={t('conversation.addPhotos')} title={t('conversation.addUpToFour')}>
                  <input type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={chooseFiles} disabled={busy || files.length >= 4} />
                  <AddPhotoAlternateOutlinedIcon /><span>{t('conversation.photos')}</span>
                </PhotoButton>
                <SendButton type="button" onClick={send} disabled={busy || (!body.trim() && !files.length)}>
                  <SendOutlinedIcon />{busy ? (progress ? `${progress} %` : t('common.sending')) : t('common.send')}
                </SendButton>
              </Actions>
            </Composer>
            <ComposerHint>{pick('Astuce : Ctrl/⌘ + Entrée pour envoyer.', 'Tip: Ctrl/⌘ + Enter to send.')}</ComposerHint>
            {error && <Error role="alert">{error}</Error>}
          </ConversationBody>
        )}
      </Panel>

      {viewer && currentAttachment && typeof document !== 'undefined' && createPortal(
        <Lightbox role="dialog" aria-modal="true" aria-label={t('conversation.gallery')} onMouseDown={(event) => { if (event.target === event.currentTarget) setViewer(null); }}>
          <LightboxCard>
            <LightboxHeader>
              <div>
                <strong>{t('conversation.sharedBy', { name: viewer.sender })}</strong>
                <span>{t('conversation.position', { index: viewer.index + 1, total: viewer.attachments.length })}</span>
              </div>
              <LightboxActions>
                <LightboxAction type="button" onClick={downloadCurrentImage}><DownloadOutlinedIcon /><span>{t('conversation.saveShare')}</span></LightboxAction>
                <LightboxAction type="button" onClick={openCurrentImage}><OpenInNewOutlinedIcon /><span>{t('conversation.openImage')}</span></LightboxAction>
                <CloseButton type="button" onClick={() => setViewer(null)} aria-label={t('conversation.closeGallery')}><CloseOutlinedIcon /></CloseButton>
              </LightboxActions>
            </LightboxHeader>
            <LightboxStage onPointerDown={onGalleryPointerDown} onPointerUp={onGalleryPointerUp} onPointerCancel={() => { pointerStartRef.current = null; }}>
              {viewer.attachments.length > 1 && <NavButton $left type="button" onClick={() => moveViewer(-1)} aria-label={t('conversation.previousPhoto')}><ChevronLeftOutlinedIcon /></NavButton>}
              <FirebaseImage src={currentAttachment.url} fallbackSrc="/images/project-placeholder.svg" alt={currentAttachment.name || `${t('common.photo')} ${viewer.index + 1}`} />
              {viewer.attachments.length > 1 && <NavButton type="button" onClick={() => moveViewer(1)} aria-label={t('conversation.nextPhoto')}><ChevronRightOutlinedIcon /></NavButton>}
            </LightboxStage>
            {galleryError && <GalleryError role="alert">{galleryError}</GalleryError>}
            {viewer.attachments.length > 1 && (
              <ThumbnailStrip>
                {viewer.attachments.map((attachment, index) => (
                  <Thumbnail type="button" key={`${attachment.url}-thumb`} $active={index === viewer.index} onClick={() => setViewer((current) => ({ ...current, index }))}>
                    <FirebaseImage src={attachment.url} fallbackSrc="/images/project-placeholder.svg" alt="" />
                  </Thumbnail>
                ))}
              </ThumbnailStrip>
            )}
          </LightboxCard>
        </Lightbox>,
        document.body,
      )}
    </>
  );
};

const Panel = styled.section`
  display:grid;
  gap:${({ $expanded }) => ($expanded ? '.7rem' : '0')};
  margin-top:1rem;
  padding:.45rem;
  border:1px solid #e5dfd5;
  border-radius:1rem;
  background:#fff;
  min-width:0;
`;
const ConversationToggle = styled.button`
  width:100%;min-width:0;display:grid;grid-template-columns:auto minmax(0,1fr) auto;align-items:center;gap:.65rem;
  padding:.62rem .65rem;border:0;border-radius:.75rem;background:transparent;color:#111;font:inherit;text-align:left;cursor:pointer;
  >svg{font-size:1.15rem;color:#8b6b38;}
  &:hover{background:#faf8f4;}
`;
const ConversationHeading = styled.span`
  min-width:0;display:grid;gap:.1rem;
  strong{font-size:.84rem;}
  span{overflow:hidden;color:#726b61;font-size:.7rem;line-height:1.4;text-overflow:ellipsis;white-space:nowrap;}
`;
const ToggleIcon = styled.span`
  display:grid;place-items:center;width:2rem;height:2rem;border-radius:50%;background:#f1ece5;color:#111;
  transform:rotate(${({ $expanded }) => ($expanded ? '180deg' : '0deg')});transition:transform .18s ease;
  svg{font-size:1.15rem;}
`;
const ConversationBody = styled.div`display:grid;gap:.65rem;padding:0 .35rem .35rem;min-width:0;`;
const MessageList = styled.div`display:grid;gap:.55rem;max-height:min(42dvh,24rem);overflow:auto;padding:.15rem;scroll-behavior:smooth;overscroll-behavior:contain;`;
const Message = styled.article`
  justify-self:${({$own})=>$own?'end':'start'};max-width:min(88%,34rem);padding:.65rem .72rem;border-radius:${({$own})=>$own?'1rem 1rem .25rem 1rem':'1rem 1rem 1rem .25rem'};background:${({$own})=>$own?'#111':'#f3efe8'};color:${({$own})=>$own?'#fff':'#111'};
  p{margin:.25rem 0;white-space:pre-wrap;font-size:.78rem;line-height:1.5;overflow-wrap:anywhere;}small{display:block;margin-top:.25rem;font-size:.58rem;opacity:.65;text-align:right;}
`;
const MessageAuthor = styled.div`display:flex;align-items:center;gap:.35rem;strong{font-size:.66rem;opacity:.78;}`;
const MiniAvatar = styled(FirebaseImage)`width:1.35rem;height:1.35rem;border-radius:50%;object-fit:cover;background:#ded8cf;`;
const AttachmentGrid = styled.div`display:grid;grid-template-columns:${({$count})=>$count===1?'minmax(0,18rem)':'repeat(2,minmax(0,1fr))'};gap:.3rem;margin-top:.35rem;`;
const AttachmentButton = styled.button`position:relative;display:block;overflow:hidden;padding:0;border:0;border-radius:.65rem;background:#e8e4dd;cursor:zoom-in;img{display:block;width:100%;height:12rem;object-fit:cover;}@media(max-width:34rem){img{height:9rem;}}`;
const AttachmentBadge = styled.span`position:absolute;right:.35rem;bottom:.35rem;padding:.2rem .38rem;border-radius:999px;background:rgba(0,0,0,.72);color:#fff;font-size:.58rem;font-weight:800;`;
const Empty = styled.p`margin:0;padding:.8rem;border:1px dashed #ddd3c4;border-radius:.8rem;color:#746e66;font-size:.73rem;line-height:1.5;text-align:center;`;
const Previews = styled.div`display:flex;gap:.45rem;overflow:auto;padding:.1rem;`;
const Preview = styled.div`position:relative;flex:0 0 4.5rem;height:4.5rem;overflow:hidden;border:1px solid #ddd3c4;border-radius:.7rem;img{width:100%;height:100%;object-fit:cover;}button{position:absolute;right:.18rem;top:.18rem;width:1.45rem;height:1.45rem;display:grid;place-items:center;border:0;border-radius:50%;background:rgba(0,0,0,.78);color:#fff;cursor:pointer;}svg{font-size:.85rem;}`;
const Composer = styled.div`position:sticky;bottom:0;z-index:2;display:grid;grid-template-columns:minmax(0,1fr) auto;align-items:end;gap:.55rem;padding-top:.3rem;background:#fff;textarea{width:100%;min-width:0;min-height:3rem;max-height:8rem;resize:vertical;padding:.65rem .75rem;border:1px solid #ddd3c4;border-radius:.8rem;font:inherit;font-size:.82rem;line-height:1.45;&:focus{outline:0;border-color:#a88a5d;box-shadow:0 0 0 3px rgba(192,163,117,.18);}}@media(max-width:34rem){grid-template-columns:1fr;}`;
const Actions = styled.div`display:flex;align-items:center;gap:.4rem;@media(max-width:34rem){justify-content:flex-end;}`;
const PhotoButton = styled.label`position:relative;display:inline-flex;align-items:center;justify-content:center;gap:.3rem;min-height:2.55rem;padding:.55rem .7rem;border:1px solid #ddd3c4;border-radius:999px;background:#fff;color:#111;font-size:.7rem;font-weight:800;cursor:pointer;input{position:absolute;width:1px;height:1px;opacity:0;pointer-events:none;}svg{font-size:1rem;}`;
const SendButton = styled.button`display:inline-flex;align-items:center;justify-content:center;gap:.35rem;min-height:2.55rem;padding:.55rem .8rem;border:0;border-radius:999px;background:#111;color:#fff;font:inherit;font-size:.72rem;font-weight:850;cursor:pointer;svg{font-size:.95rem;}&:disabled{opacity:.5;cursor:not-allowed;}`;
const ComposerHint = styled.small`margin-top:-.25rem;color:#8a837b;font-size:.62rem;line-height:1.35;@media(max-width:34rem){display:none;}`;
const Error = styled.p`margin:0;color:#9a302a;font-size:.7rem;`;
const Lightbox = styled.div`
  position: fixed;
  inset: 0;
  z-index: 20000;
  display: grid;
  place-items: center;
  padding: max(1rem, env(safe-area-inset-top)) max(1rem, env(safe-area-inset-right)) max(1rem, env(safe-area-inset-bottom)) max(1rem, env(safe-area-inset-left));
  background: rgba(4,5,6,.96);
  overscroll-behavior: none;
  touch-action: none;

  @media(max-width:640px){
    place-items: stretch;
    padding: 0;
  }
`;
const LightboxCard = styled.div`
  width:min(72rem,100%);
  max-height:calc(100dvh - 2rem);
  display:grid;
  grid-template-rows:auto minmax(0,1fr) auto;
  overflow:hidden;
  border:1px solid rgba(255,255,255,.16);
  border-radius:1.15rem;
  background:#090a0b;
  box-shadow:0 2rem 6rem rgba(0,0,0,.55);

  @media(max-width:640px){
    width:100%;
    height:100vh;
    height:100svh;
    height:100dvh;
    max-height:none;
    border:0;
    border-radius:0;
  }
`;
const LightboxHeader = styled.header`
  position:relative;
  z-index:4;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:.65rem;
  min-height:4.1rem;
  padding:.7rem .8rem;
  color:#fff;
  background:linear-gradient(180deg,rgba(0,0,0,.78),rgba(0,0,0,.35));
  div:first-child{display:grid;gap:.08rem;min-width:0;}
  strong{font-size:.78rem;overflow-wrap:anywhere;}
  span{color:#c3c9cd;font-size:.63rem;}

  @media(max-width:640px){
    padding:calc(env(safe-area-inset-top) + .55rem) max(.55rem,env(safe-area-inset-right)) .6rem max(.7rem,env(safe-area-inset-left));
  }
`;
const LightboxActions = styled.div`display:flex;align-items:center;gap:.35rem;flex:0 0 auto;`;
const LightboxAction = styled.button`
  display:inline-flex;align-items:center;gap:.3rem;min-height:2.35rem;padding:.45rem .65rem;
  border:1px solid rgba(255,255,255,.25);border-radius:999px;background:rgba(255,255,255,.1);color:#fff;
  font:inherit;font-size:.66rem;font-weight:800;cursor:pointer;svg{font-size:1rem;}
  @media(max-width:34rem){span{display:none;}width:2.35rem;padding:0;justify-content:center;}
`;
const CloseButton = styled.button`
  width:2.55rem;height:2.55rem;display:grid;place-items:center;border:0;border-radius:50%;
  background:#fff;color:#111;cursor:pointer;box-shadow:0 .4rem 1.2rem rgba(0,0,0,.35);svg{font-size:1.2rem;}
`;
const LightboxStage = styled.div`
  position:relative;min-height:16rem;display:grid;place-items:center;overflow:hidden;background:#050607;
  touch-action:none;user-select:none;-webkit-user-select:none;
  >img{width:100%;height:100%;max-height:calc(100dvh - 10rem);object-fit:contain;-webkit-user-drag:none;}
  @media(max-width:640px){min-height:0;>img{max-height:none;}}
`;
const NavButton = styled.button`
  position:absolute;z-index:2;${({$left})=>$left?'left:.65rem':'right:.65rem'};top:50%;transform:translateY(-50%);
  width:2.8rem;height:2.8rem;display:grid;place-items:center;border:1px solid rgba(255,255,255,.25);border-radius:50%;
  background:rgba(0,0,0,.55);color:#fff;cursor:pointer;svg{font-size:1.6rem;}
  @media(max-width:640px){display:none;}
`;
const GalleryError = styled.p`margin:0;padding:.55rem .75rem;background:#351817;color:#ffd8d4;font-size:.66rem;line-height:1.4;text-align:center;`;
const ThumbnailStrip = styled.div`
  display:flex;justify-content:center;gap:.4rem;overflow-x:auto;
  padding:.6rem max(.6rem,env(safe-area-inset-right)) calc(.6rem + env(safe-area-inset-bottom)) max(.6rem,env(safe-area-inset-left));
  background:#101214;
`;
const Thumbnail = styled.button`flex:0 0 3.4rem;width:3.4rem;height:3.4rem;overflow:hidden;padding:0;border:${({$active})=>$active?'2px solid #c0a375':'2px solid transparent'};border-radius:.55rem;background:#222;cursor:pointer;img{width:100%;height:100%;object-fit:cover;}`;

export default connect(({ firebase: firebaseState }) => ({ firebaseState }))(ProjectConversationPanel);
